import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Shield, Eye, AlertTriangle, Users, Trash2, MapPin, RefreshCw, MessageSquare, Clock, User, ChevronRight } from 'lucide-react';

const pinTypes = {
  blue: { label: 'Eyes on the Street', icon: Eye, color: 'bg-blue-500' },
  red: { label: 'Urgent Concern', icon: AlertTriangle, color: 'bg-red-500' },
  yellow: { label: 'Community Watch', icon: Users, color: 'bg-amber-500' }
};

const statusOptions = [
  { value: 'active', label: 'Active', color: 'bg-emerald-500' },
  { value: 'investigating', label: 'Investigating', color: 'bg-plp-gold' },
  { value: 'resolved', label: 'Resolved', color: 'bg-blue-500' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-500' }
];

const AdminSafetyPins = () => {
  const { token } = useAuth();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedPin, setSelectedPin] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    note: '',
    status: ''
  });

  const fetchPins = async () => {
    try {
      const params = filter !== 'all' ? { pin_type: filter, include_all: true } : { include_all: true };
      const response = await axios.get(`${API}/safety-pins`, { params });
      setPins(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPins(); }, [filter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this safety report?')) return;
    try {
      await axios.delete(`${API}/safety-pins/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Report deleted!');
      fetchPins();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleOpenUpdate = async (pin) => {
    // Fetch full pin details to get updates
    try {
      const response = await axios.get(`${API}/safety-pins/${pin.id}`);
      setSelectedPin(response.data);
      setUpdateForm({ note: '', status: response.data.status || 'active' });
      setShowUpdateDialog(true);
    } catch (error) {
      toast.error('Failed to load report details');
    }
  };

  const handleAddNote = async () => {
    if (!updateForm.note.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setUpdating(true);
    try {
      await axios.post(`${API}/safety-pins/${selectedPin.id}/notes`, 
        { note: updateForm.note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note added successfully');
      
      // Refresh pin data
      const response = await axios.get(`${API}/safety-pins/${selectedPin.id}`);
      setSelectedPin(response.data);
      setUpdateForm(prev => ({ ...prev, note: '' }));
      fetchPins();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === selectedPin.status) return;

    setUpdating(true);
    try {
      await axios.patch(`${API}/safety-pins/${selectedPin.id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      
      // Refresh pin data
      const response = await axios.get(`${API}/safety-pins/${selectedPin.id}`);
      setSelectedPin(response.data);
      setUpdateForm(prev => ({ ...prev, status: newStatus }));
      fetchPins();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const counts = {
    all: pins.length,
    blue: pins.filter(p => p.pin_type === 'blue').length,
    red: pins.filter(p => p.pin_type === 'red').length,
    yellow: pins.filter(p => p.pin_type === 'yellow').length
  };

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status) || statusOptions[0];
    return <Badge className={`${opt.color} text-white`}>{opt.label}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" /> Safety Reports
          </h1>
          <p className="text-sm sm:text-base text-slate-600">View, manage, and update community safety reports</p>
        </div>
        <Button variant="outline" onClick={fetchPins} data-testid="refresh-safety-btn" className="self-start sm:self-auto">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filter Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className={`cursor-pointer ${filter === 'all' ? 'ring-2 ring-plp-blue' : ''}`} onClick={() => setFilter('all')}>
          <CardContent className="p-4 text-center">
            <Shield className="w-6 h-6 mx-auto mb-2 text-plp-blue" />
            <p className="text-2xl font-bold">{counts.all}</p>
            <p className="text-sm text-slate-500">All Reports</p>
          </CardContent>
        </Card>
        {Object.entries(pinTypes).map(([key, config]) => (
          <Card key={key} className={`cursor-pointer ${filter === key ? 'ring-2 ring-plp-blue' : ''}`} onClick={() => setFilter(key)}>
            <CardContent className="p-4 text-center">
              <config.icon className={`w-6 h-6 mx-auto mb-2 ${config.color.replace('bg-', 'text-')}`} />
              <p className="text-2xl font-bold">{counts[key]}</p>
              <p className="text-sm text-slate-500">{config.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : pins.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-12 text-center"><Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No safety reports</p></CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pins.map((pin) => {
            const config = pinTypes[pin.pin_type] || pinTypes.blue;
            return (
              <Card key={pin.id} className="bg-white" data-testid={`safety-pin-${pin.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${config.color} text-white`}>
                          <config.icon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                        {getStatusBadge(pin.status)}
                      </div>
                      <h3 className="font-heading font-semibold">{pin.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{pin.description}</p>
                      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                        <MapPin className="w-3 h-3" />
                        {pin.latitude.toFixed(4)}, {pin.longitude.toFixed(4)}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(pin.created_at).toLocaleString()}
                      </p>
                      {pin.updates?.length > 0 && (
                        <p className="text-xs text-plp-blue mt-2 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {pin.updates.length} update{pin.updates.length > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleOpenUpdate(pin)} 
                        className="text-plp-blue hover:bg-plp-blue/10"
                        data-testid={`update-safety-${pin.id}`}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(pin.id)} 
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Update Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Safety Report Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedPin && (
            <div className="space-y-4 mt-4">
              {/* Report Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${pinTypes[selectedPin.pin_type]?.color || 'bg-blue-500'} text-white`}>
                    {pinTypes[selectedPin.pin_type]?.label || 'Report'}
                  </Badge>
                  {getStatusBadge(selectedPin.status)}
                </div>
                <h3 className="font-heading font-semibold">{selectedPin.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{selectedPin.description}</p>
                <p className="text-xs text-slate-400 mt-2">
                  Reported: {new Date(selectedPin.created_at).toLocaleString()}
                </p>
                {selectedPin.user_email && (
                  <p className="text-xs text-plp-blue mt-1">
                    Reporter: {selectedPin.user_email}
                  </p>
                )}
              </div>

              {/* Status Change */}
              <div>
                <Label>Update Status</Label>
                <Select value={updateForm.status} onValueChange={handleStatusChange} disabled={updating}>
                  <SelectTrigger data-testid="safety-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add Note */}
              <div className="space-y-2">
                <Label>Add Progress Note</Label>
                <Textarea
                  value={updateForm.note}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="Enter update or progress note..."
                  rows={3}
                  data-testid="safety-note-input"
                />
                <Button 
                  onClick={handleAddNote} 
                  disabled={updating || !updateForm.note.trim()}
                  className="bg-plp-blue hover:bg-plp-blue-light"
                  data-testid="add-safety-note-btn"
                >
                  {updating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                  Add Note
                </Button>
              </div>

              {/* Update History */}
              {selectedPin.updates?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Progress History</Label>
                  <ScrollArea className="h-48 border rounded-lg">
                    <div className="p-3 space-y-3">
                      {selectedPin.updates.slice().reverse().map((update, idx) => (
                        <div key={update.id || idx} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <User className="w-3 h-3" />
                            {update.admin_name}
                            <span className="text-slate-300">|</span>
                            <Clock className="w-3 h-3" />
                            {new Date(update.created_at).toLocaleString()}
                          </div>
                          {update.status_change && (
                            <Badge variant="outline" className="text-xs mb-1">
                              {update.status_change}
                            </Badge>
                          )}
                          <p className="text-sm text-slate-700">{update.note}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSafetyPins;
